import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_elite-scout-mvp/artifacts/uw8m7f8v_Simple%20Sports%20Football%20%26%20Soccer%20Badge%20Logo.png";
const MANAGEMENT_EMAIL = "management@elitescoutingnetwork.com";

const SERVICES = [
  {
    label: "Career Planning",
    body: "A structured view of where a career sits today, where it can go, and the moves required to get there. Private, considered, long-term.",
  },
  {
    label: "Club Conversations",
    body: "Discreet conversations with the right clubs at the right time. No blanket outreach. No mass-mailed profiles.",
  },
  {
    label: "Opportunity Filtering",
    body: "Not every interest is a good interest. Opportunities are assessed, weighed, and filtered against your career direction before they reach you.",
  },
  {
    label: "Transfer Window Positioning",
    body: "Preparation before windows open. Positioning inside windows. Reviewing and resetting after windows close.",
  },
  {
    label: "Contract & Commercial Review",
    body: "Considered review of contractual terms and commercial positioning, with the right specialists brought in where needed.",
  },
  {
    label: "Presentation & Profile",
    body: "How you are presented matters. Structured profiles, considered messaging, consistent positioning across every club-facing touchpoint.",
  },
];

const PROCESS = [
  { n: "01", t: "Private enquiry", b: "A considered introduction — not a form, not a funnel." },
  { n: "02", t: "Assessment", b: "A proper review of profile, career direction, and current positioning." },
  { n: "03", t: "Engagement", b: "If it's right for both sides, we work together." },
];

export default function PlayerManagement() {
  return (
    <div
      className="bg-[#0A0A0A] text-[#F5F5F5] min-h-screen"
      data-testid="player-management-page"
      style={{ fontFamily: "'Cabinet Grotesk', 'Inter', sans-serif" }}
    >
      {/* Minimal brand bar — discreet, no menu */}
      <div className="w-full border-b border-[#141414]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-6 w-6 rounded-full opacity-80" />
          <span className="text-[10px] text-[#777] tracking-[0.3em] uppercase">
            Elite Scouting Network
          </span>
        </div>
      </div>

      {/* HERO */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-24 sm:pt-32 pb-20 sm:pb-28" data-testid="pm-hero">
        <p className="text-[10px] sm:text-xs text-[#B89325] tracking-[0.36em] uppercase mb-8">
          Player Management
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.05] text-[#F5F5F5] mb-8" data-testid="pm-headline">
          Considered representation for players who take their careers
          <span className="text-[#D4AF37] font-normal"> seriously</span>.
        </h1>
        <p className="text-lg sm:text-xl text-[#9a9a9a] leading-[1.6] font-light max-w-2xl">
          A private, long-term approach to player representation. Built for
          professional and advanced semi-professional players who want to be
          positioned properly — not scattered across the market.
        </p>
      </section>

      {/* DIVIDER LINE */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="h-px bg-[#1E1E1E]"></div>
      </div>

      {/* STATEMENT */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-24 sm:py-32 text-center" data-testid="pm-statement">
        <p className="text-2xl sm:text-3xl md:text-4xl font-light leading-[1.4] text-[#e8e8e8]">
          Representation should be quiet, structured,
        </p>
        <p className="text-2xl sm:text-3xl md:text-4xl font-normal leading-[1.4] text-[#D4AF37] mt-2">
          and genuinely in the player's interest.
        </p>
      </section>

      {/* DIVIDER LINE */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="h-px bg-[#1E1E1E]"></div>
      </div>

      {/* SERVICES */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="pm-services">
        <div className="mb-14 sm:mb-20">
          <p className="text-[10px] text-[#B89325] tracking-[0.36em] uppercase mb-5">Services</p>
          <h2 className="text-3xl sm:text-4xl font-light tracking-[-0.01em] text-[#F5F5F5] max-w-2xl">
            What we do — and don't do.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12 gap-x-16 lg:gap-x-24">
          {SERVICES.map((s) => (
            <div key={s.label} className="pb-2">
              <h3 className="text-lg font-normal text-[#F5F5F5] mb-3 pb-3 border-b border-[#1E1E1E]">
                {s.label}
              </h3>
              <p className="text-[14px] text-[#9a9a9a] leading-[1.75] font-light">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* DIVIDER LINE */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="h-px bg-[#1E1E1E]"></div>
      </div>

      {/* PROCESS */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="pm-process">
        <div className="mb-14 sm:mb-20">
          <p className="text-[10px] text-[#B89325] tracking-[0.36em] uppercase mb-5">Process</p>
          <h2 className="text-3xl sm:text-4xl font-light tracking-[-0.01em] text-[#F5F5F5] max-w-2xl">
            How engagement works.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          {PROCESS.map((p) => (
            <div key={p.n}>
              <p className="text-[11px] text-[#B89325] tracking-[0.28em] mb-5">{p.n}</p>
              <h3 className="text-xl font-normal text-[#F5F5F5] mb-3">{p.t}</h3>
              <p className="text-[14px] text-[#9a9a9a] leading-[1.75] font-light">{p.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DIVIDER LINE */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="h-px bg-[#1E1E1E]"></div>
      </div>

      {/* WHO */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-24 sm:py-32 text-center" data-testid="pm-who">
        <p className="text-[10px] text-[#B89325] tracking-[0.36em] uppercase mb-8">Who this is for</p>
        <p className="text-xl sm:text-2xl md:text-3xl font-light leading-[1.45] text-[#e8e8e8]">
          Professional and advanced semi-professional players
          <br className="hidden sm:block" /> seeking considered, long-term representation —
          <br className="hidden sm:block" /> <span className="text-[#D4AF37]">not volume outreach</span>.
        </p>
      </section>

      {/* DIVIDER LINE */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="h-px bg-[#1E1E1E]"></div>
      </div>

      {/* CONTACT */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-24 sm:py-36 text-center" data-testid="pm-contact">
        <p className="text-[10px] text-[#B89325] tracking-[0.36em] uppercase mb-8">Private enquiry</p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.01em] leading-[1.15] text-[#F5F5F5] mb-12">
          If this is the level of representation you're looking for,
          <br className="hidden sm:block" />
          <span className="text-[#D4AF37] font-normal">introduce yourself privately</span>.
        </h2>

        <a
          href={`mailto:${MANAGEMENT_EMAIL}?subject=Player%20Management%20Enquiry`}
          className="inline-flex items-center gap-3 text-[#F5F5F5] hover:text-[#D4AF37] transition-colors border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-1.5 text-base sm:text-lg tracking-wide"
          data-testid="pm-contact-email"
        >
          {MANAGEMENT_EMAIL}
          <ArrowUpRight size={16} />
        </a>

        <p className="text-[11px] text-[#6C6C6C] mt-10 leading-[1.8] tracking-wide max-w-md mx-auto">
          All enquiries are read personally and treated with full discretion.
          Responses are considered. We do not engage every player who applies.
        </p>
      </section>

      {/* FOOTER — minimal */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pb-12">
        <div className="border-t border-[#141414] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="" className="h-5 w-5 rounded-full opacity-60" />
            <span className="text-[10px] text-[#555] tracking-[0.3em] uppercase">
              Elite Scouting Network
            </span>
          </div>
          <Link
            to="/"
            className="text-[10px] text-[#555] hover:text-[#D4AF37] tracking-[0.2em] uppercase transition-colors"
            data-testid="pm-back-profiles"
          >
            Player Profiles →
          </Link>
        </div>
      </div>
    </div>
  );
}
